/**
 * PhotoCard Component
 * 
 * Displays a single photo thumbnail in the gallery grid.
 */

import React, { memo } from "react";
import { View, Text, Image, Pressable, Dimensions } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import type { PhotoWithRelations } from "../types";

const { width: SCREEN_WIDTH } = Dimensions.get("window");
const CARD_MARGIN = 2;
const CARDS_PER_ROW = 3;
const CARD_SIZE = (SCREEN_WIDTH - CARD_MARGIN * (CARDS_PER_ROW + 1) * 2) / CARDS_PER_ROW;

interface PhotoCardProps {
  photo: PhotoWithRelations;
  onPress: (photo: PhotoWithRelations) => void;
  isSelected?: boolean;
  isSelecting?: boolean;
  onToggleSelect?: (photoId: string) => void;
  showStats?: boolean;
}

/**
 * Photo thumbnail card
 */
export const PhotoCard: React.FC<PhotoCardProps> = memo(({
  photo,
  onPress,
  isSelected = false,
  isSelecting = false,
  onToggleSelect,
  showStats = true,
}) => {
  const colors = useThemeColors();

  const handlePress = () => {
    if (isSelecting && onToggleSelect) {
      onToggleSelect(photo.id);
    } else {
      onPress(photo);
    }
  };

  const handleLongPress = () => {
    if (onToggleSelect) {
      onToggleSelect(photo.id);
    }
  };

  return (
    <Pressable
      onPress={handlePress}
      onLongPress={handleLongPress}
      delayLongPress={300}
      style={{
        width: CARD_SIZE,
        height: CARD_SIZE,
        margin: CARD_MARGIN,
      }}
      accessibilityRole="button"
      accessibilityLabel={`Foto${photo.description ? `: ${photo.description}` : ""}`}
    >
      {/* Image */}
      <Image
        source={{ uri: photo.thumbnail_url || photo.file_url }}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 8,
          backgroundColor: colors.surfaceSecondary,
        }}
        resizeMode="cover"
      />

      {/* Selection overlay */}
      {isSelecting && (
        <View
          className="absolute inset-0 rounded-lg"
          style={{
            backgroundColor: isSelected ? `${colors.primary}40` : "transparent",
            borderWidth: isSelected ? 3 : 0,
            borderColor: colors.primary,
            borderRadius: 8,
          }}
        >
          <View
            className="absolute top-2 right-2 w-6 h-6 rounded-full items-center justify-center"
            style={{
              backgroundColor: isSelected ? colors.primary : `${colors.background}90`,
              borderWidth: isSelected ? 0 : 2,
              borderColor: colors.textSecondary,
            }}
          >
            {isSelected && (
              <Ionicons name="checkmark" size={16} color="#FFFFFF" />
            )}
          </View>
        </View>
      )}

      {/* Stats overlay */}
      {showStats && !isSelecting && (photo.likes_count > 0 || photo.comments_count > 0) && (
        <View
          className="absolute bottom-0 left-0 right-0 flex-row items-center justify-end px-2 py-1 rounded-b-lg"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          {photo.likes_count > 0 && (
            <View className="flex-row items-center mr-2">
              <Ionicons name="heart" size={12} color="#FFFFFF" />
              <Text className="text-white text-xs ml-1">{photo.likes_count}</Text>
            </View>
          )}
          {photo.comments_count > 0 && (
            <View className="flex-row items-center">
              <Ionicons name="chatbubble" size={12} color="#FFFFFF" />
              <Text className="text-white text-xs ml-1">{photo.comments_count}</Text>
            </View>
          )}
        </View>
      )}

      {/* Featured badge */}
      {photo.is_featured && (
        <View
          className="absolute top-2 left-2 px-1.5 py-0.5 rounded"
          style={{ backgroundColor: "#F59E0B" }}
        >
          <Ionicons name="star" size={12} color="#FFFFFF" />
        </View>
      )}

      {/* Hidden badge (for super admins) */}
      {!photo.is_visible && (
        <View
          className="absolute top-2 left-2 px-1.5 py-0.5 rounded"
          style={{ backgroundColor: colors.error }}
        >
          <Ionicons name="eye-off" size={12} color="#FFFFFF" />
        </View>
      )}
    </Pressable>
  );
});

PhotoCard.displayName = "PhotoCard";

export default PhotoCard;

